<template>
    <div :data-uuid="settings.uuid" :style="containerStyle">
        <label v-if="general.name && !isCheckbox()" :style="labelStyle" :for="settings.uuid">
            {{ general.name }} <span style="color: #dd4141" v-if="general.isRequired && !general.isHidden">*</span>
        </label>
        <template v-if="type() === 'password' || type() === 'password_confirm'">
            <button :style="eyeStyles" @keydown.enter.prevent tabindex="-1" type="button" @click="isHidden = !isHidden">
                <EyeStrokedIcon width="20" height="20" v-if="isHidden" />
                <EyeIcon fill="#3965FF" width="20" height="20" v-else />
            </button>
        </template>

        <input
            tabindex="-1"
            v-if="getValidation()"
            type="text"
            style="
                height: 1px;
                padding: 0;
                margin: 0;
                outline: none;
                position: absolute;
                opacity: 0;
                pointer-events: none;
            "
            :required="general.isRequired && !general.isHidden"
            :name="`${getName()}_validation`"
            :value="getValidation()"
        />

        <textarea
            @input="handleInput"
            :hidden="general.isHidden"
            :required="general.isRequired && !general.isHidden"
            :style="textAreaStyle"
            :value="general.value"
            :placeholder="general.placeholder"
            :id="settings.uuid"
            :readonly="isDevMode()"
            :name="getName()"
            v-if="isTextArea()"
        ></textarea>

        <DefaultPhoneInput
            v-else-if="isPhone()"
            :inputStyle="inputStyle"
            :value="getPhoneValue()"
            @update="setPhoneValue"
            :readonly="isDevMode()"
            :placeholder="general.placeholder"
            :format="general.format"
            :validation="general.validation"
            :name="getName()"
            :uuid="settings.uuid"
            :id="settings.uuid"
            :errors="[]"
            :required="general.isRequired && !general.isHidden"
        />

        <div :style="checkPointer()" v-else-if="isCheckbox()" class="checkbox-container">
            <input
                tabindex="-1"
                type="text"
                style="height: 1px; padding: 0; margin: 0; outline: none; position: absolute; opacity: 0"
                :required="general.isRequired && !general.isHidden"
                :name="getName()"
                :value="(general.value && general.value) || ''"
            />
            <DefaultCheck class="mr-10 mt-2" @update="general.value = $event" :checked="!!general.value" />
            <p style="margin-top: 0" class="cursor-pointer" @click="general.value = !general.value">
                <span v-html="general.name" class="text-view"></span>
            </p>
        </div>

        <template v-else-if="isDate()">
            <input
                tabindex="-1"
                type="text"
                style="height: 1px; padding: 0; margin: 0; outline: none; position: absolute; opacity: 0"
                :required="general.isRequired && !general.isHidden"
                :name="getName()"
                :value="general.value || ''"
            />
            <DefaultDateDropdown
                :format="general.format"
                :validation="general.validation"
                flexible-label
                :style="checkPointer()"
                @update="handleValue"
                :loading="loading"
                :cases="general.values"
                :active-case="general.value || ''"
                :id="settings.uuid"
                :required="general.isRequired && !general.isHidden"
            />
        </template>

        <template v-else-if="isDropdown()">
            <input
                tabindex="-1"
                type="text"
                style="height: 1px; padding: 0; margin: 0; outline: none; position: absolute; opacity: 0"
                :required="general.isRequired && !general.isHidden"
                :name="getName()"
                :value="(general.value && general.value.id) || ''"
            />

            <DefaultCountriesDropdown
                :format="general.format"
                :validation="general.validation"
                :border-color="colors.border"
                flexible-label
                :style="checkPointer()"
                @update="updateField($event)"
                :loading="loading"
                :placeholder="general.placeholder"
                :cases="general.values || []"
                :country="general.value || ''"
                :id="settings.uuid"
                :required="general.isRequired && !general.isHidden"
                v-if="isCountry()"
            />

            <DefaultDropdown
                v-else
                @update="updateField($event)"
                :style="checkPointer()"
                :border-color="colors.border"
                :background="colors.background"
                :required="general.isRequired && !general.isHidden"
                :loading="loading"
                :id="settings.uuid"
                :active-case="general.value || ''"
                :with-search="general.values && general.values.length > 10"
                :placeholder="general.placeholder"
                :cases="general.values || []"
            />
        </template>
        <input
            v-else
            @input="handleInput"
            :value="general.value"
            :readonly="isDevMode()"
            :hidden="general.isHidden"
            :required="general.isRequired && !general.isHidden"
            :style="inputStyle"
            :placeholder="general.placeholder"
            :id="settings.uuid"
            :name="getName()"
            :type="getType()"
        />

        <component :is="'style'">
            {{ childrenStyle }}
        </component>
    </div>
</template>

<script>
import DefaultCheck from "../../../Forms/DefaultCheck.vue"
import DefaultCountriesDropdown from "../../../Forms/country/DefaultCountriesDropdown.vue"
import DefaultDateDropdown from "../../../Forms/DefaultDateDropdown.vue"
import DefaultPhoneInput from "../../../Forms/phone/DefaultPhoneInput.vue"
import ViewMixin from "../utils/view-mixin"
import { resolveFont, resolveMargin, resolvePadding, toPx } from "../utils/style"
import DefaultDropdown from "../../../Forms/DefaultDropdown.vue"
import EyeStrokedIcon from "../../../../icons/EyeStrokedIcon.vue"
import EyeIcon from "../../../../icons/EyeIcon.vue"

import "./styles.css"

const APP_DOMAIN =
    window.app_domain === "kwiga.local"
        ? "http://kwiga.local"
        : window.app_domain === "kwiga3.link"
        ? "https://kwiga3.link"
        : "https://kwiga.com"

const BASE_URL = `${APP_DOMAIN}/api/v1`

export default {
    components: {
        DefaultCheck,
        DefaultDateDropdown,
        DefaultCountriesDropdown,
        DefaultPhoneInput,
        EyeIcon,
        EyeStrokedIcon,
        DefaultDropdown
    },
    props: {
        settings: {
            type: Object,
            default: () => {}
        }
    },
    mixins: [ViewMixin],
    name: "WidgetField",
    data() {
        return {
            loading: false,
            isHidden: true,
            updating: false,
            clonedValue: null
        }
    },
    created() {
        /*if (this.settings.general.value && !this.general.isHidden) {
            this.settings.general.value = ""
        }*/

        this.handleType()
    },
    methods: {
        checkPointer() {
            return {
                pointerEvents: this.isDevMode() ? "none" : "all"
            }
        },
        handleType() {
            const toPreloadFieldsMap = {
                timezone: this.getTimezones,
                currency: this.getCurrencies,
                country: this.getCountries
            }
            setTimeout(async () => {
                if (toPreloadFieldsMap[this.type()]) {
                    this.loading = true

                    this.general.values = await toPreloadFieldsMap[this.type()]()

                    this.setDefaults()

                    this.loading = false
                }
            })

            if (this.type() === "dropdown") {
                for (const value of this.general.values) {
                    value.name = value.value
                }
            }

            this.setDefaults()
        },
        setDefaults() {
            this.clonedValue = this.general.value

            if (this.type() === "country" && this.general.format && "default_country" in this.general.format) {
                let defaultCountry = null

                if (this.general.format.default_country) {
                    if (this.general.format.default_country.iso2 === "auto") {
                        defaultCountry = this.general.values.find(
                            c => c && c.iso_2 && c.iso_2.toLowerCase() === window.country.toLowerCase()
                        )
                    } else {
                        defaultCountry = this.general.values.find(
                            c => c && c.iso_2 && c.iso_2.toLowerCase() === this.general.format.default_country.iso2
                        )
                    }
                }

                this.general.value = defaultCountry || null
            }

            if (this.type() === "date" && this.general.format && this.general.format.default_date) {
                if (this.general.format.default_date === "now") {
                    this.general.value = new Date()
                } else {
                    this.general.value = new Date(this.general.format.default_date)
                }
            }
        },
        type() {
            if (this.settings.uuid && this.settings.uuid.startsWith("coupon")) {
                return "coupon"
            }
            return this.general.type && this.general.type.type
        },
        isTextArea() {
            return this.type() === "textarea"
        },
        isPhone() {
            return this.type() === "phone"
        },
        isCheckbox() {
            return this.type() === "checkbox"
        },
        isDate() {
            return this.type() === "date"
        },
        isCountry() {
            return this.type() === "country"
        },
        isDropdown() {
            const type = this.type()
            return type === "timezone" || type === "currency" || type === "dropdown" || type === "country"
        },
        updateField(event) {
            this.updating = true

            this.settings.general = {
                ...this.settings.general,
                value: {
                    ...event
                }
            }
            this.$nextTick(() => {
                this.updating = false
            })
        },
        handleValue(value) {
            this.updating = true

            this.settings.general.value = value

            this.$nextTick(() => {
                this.updating = false
            })
        },
        handleInput(event) {
            this.updating = true

            this.settings.general = {
                ...this.settings.general,
                value: event.target.value
            }

            this.$nextTick(() => {
                this.updating = false
            })
        },
        getValidation() {
            if (this.general.validation) {
                return JSON.stringify({
                    is_required: this.general.isRequired && !this.general.isHidden,
                    ...this.general.validation
                })
            }
            return ""
        },
        getName() {
            if (this.general.isDefault) {
                return this.type()
            }
            return `field_${this.general.id}`
        },
        getType() {
            if (!this.general.type) {
                return ""
            }
            if (this.type() === "email") {
                return "email"
            }
            if (this.type() === "password" && this.isHidden) {
                return "password"
            }
            if (this.type() === "password_confirm" && this.isHidden) {
                return "password"
            }
            return ""
        },
        async getCurrencies() {
            const response = await fetch(`${BASE_URL}/currencies`)

            const result = await response.json()

            if (result) {
                return result.data.map(c => {
                    return {
                        ...c,
                        name: `${c.code} ${c.code ? "- " : ""}${c.html_code}`
                    }
                })
            }
            return []
        },
        async getTimezones() {
            const response = await fetch(`${BASE_URL}/timezones`)

            const result = await response.json()

            if (result) {
                return result.data.map(t => {
                    let utc = t.value.replace(/UTC/g, "")
                    t.originalName = t.name
                    t.name = `${utc} ${t.name}`
                    return t
                })
            }
            return []
        },
        async getCountries() {
            const response = await fetch(`${BASE_URL}/countries`)

            const result = await response.json()

            if (result) {
                return result.data
            }
            return []
        },
        isDevMode() {
            return window.builder_dev_mode
        },
        setPhoneValue(value) {
            if (this.isDevMode()) {
                this.updating = true

                this.clonedValue = value

                this.$nextTick(() => {
                    this.updating = false
                })

                return
            }

            this.handleValue(value)
        },
        getPhoneValue() {
            if (this.isDevMode()) {
                return this.clonedValue
            }
            return this.general.value
        }
    },
    computed: {
        containerStyle() {
            const style = {
                ...this.resolveStyle(
                    {
                        maxWidth: toPx(this.desktop.width.value),
                        minHeight: toPx(this.desktop.height.value),
                        width: "100%",
                        ...resolveMargin(this.desktop.margin)
                    },
                    {
                        width: "100%",
                        minWidth: "100%",
                        minHeight: toPx(this.mobile.height.value),
                        ...resolveMargin(this.mobile.margin)
                    }
                )
            }

            if (this.general.stretched) {
                style.width = "100%"
                style.maxWidth = "100%"
            }

            if (this.general.isHidden) {
                if (window.builder_dev_mode) {
                    if (!this.isDropdown()) {
                        style.opacity = 0.4
                    }
                } else {
                    style.display = "none"
                }
            }
            return style
        },
        childrenStyle() {
            return `
                #${this.settings.uuid}.default-dropdown {
                    font-family: ${resolveFont(this.fonts.text).fontFamily};
                    font-size: ${resolveFont(this.fonts.text).fontSize};
                }
                #${this.settings.uuid} .default-dropdown__current {
                    border-radius: 4px !important;
                    color: ${this.colors.text};
                    font-size: ${resolveFont(this.fonts.text).fontSize};
                    padding: ${resolvePadding(this.desktop.padding).padding};
                    height: ${toPx(this.desktop.height.value)};
                    box-sizing: border-box;
                    opacity: ${this.general.isHidden && window.builder_dev_mode ? 0.4 : 1};
                    background: ${this.colors.background} !important;
                }
                #${this.settings.uuid} .default-dropdown__current input {
                    background: ${this.colors.background} !important;
                }
                #${this.settings.uuid} .default-phone-handler {
                    background-color: inherit;
                }
                #${this.settings.uuid} .default-phone-input__input {
                    background-color: inherit;
                }
                #${this.settings.uuid} .default-dropdown__placeholder {
                    color: ${this.colors.placeholder};
                }
                #${this.settings.uuid}::placeholder {
                    color: ${this.colors.placeholder};
                    font-family: ${this.fonts.placeholder.family};
                    font-size: ${this.fonts.placeholder.size};
                }
                #${this.settings.uuid}-phone::placeholder {
                    color: ${this.colors.placeholder};
                    font-family: ${this.fonts.placeholder.family};
                    font-size: ${this.fonts.placeholder.size};
                }
                #${this.settings.uuid}:focus, #${this.settings.uuid}:focus-visible {
                    border-color: ${this.colors.focus} !important;
                    outline: none;
                }
            `
        },
        textAreaStyle() {
            return {
                ...this.inputStyle,
                minHeight: "100px",
                resize: "none"
            }
        },
        inputStyle() {
            return {
                display: "block",
                width: "100%",
                borderRadius: "4px",
                boxSizing: "border-box",
                border: `1px solid ${this.colors.border}`,
                color: this.colors.text,
                background: this.colors.background,
                ...this.checkPointer(),
                ...resolveFont(this.fonts.text),
                ...this.resolveStyle(
                    {
                        ...resolvePadding(this.desktop.padding)
                    },
                    {
                        ...resolvePadding(this.mobile.padding)
                    }
                )
            }
        },
        eyeStyles() {
            return `
                padding: 0;
                height: 30px;
                width: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: transparent;
                outline: none;
                border: none;
                cursor: pointer;
                margin-top: 8px;
                margin-bottom: -40px;
                float: right;
                margin-right: 15px`
        },
        labelStyle() {
            return {
                ...resolveFont(this.fonts.name),
                display: "block",
                color: this.colors.name,
                textAlign: "left",
                opacity: this.general.isHidden && window.builder_dev_mode ? 0.4 : 1,
                ...resolveMargin(this.general && this.general.label && this.general.label.margin)
            }
        },
        general() {
            return this.settings.general
        },
        desktop() {
            return this.settings.desktop
        },
        mobile() {
            return this.settings.mobile
        },
        colors() {
            return this.settings.general.colors
        },
        fonts() {
            return this.settings.general.fonts
        }
    },
    watch: {
        "settings.general.type": {
            deep: true,
            handler(val, oldVal) {
                if (val !== oldVal) {
                    this.handleType()
                }
            }
        },
        "settings.general": {
            deep: true,
            handler() {
                if (!this.updating) {
                    this.updating = true
                    this.setDefaults()
                    this.$nextTick(() => {
                        this.updating = false
                    })
                }
            }
        }
    }
}
</script>

<style scoped>

</style>
